import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { GraduationCap, Video, FileText, BookOpen, ClipboardList, ChevronDown } from "lucide-react";
import { PageHeader, Tabs, TabsList, TabsTrigger, TabsContent } from "../ui";
import RecordingsListPage from "../BatchRecordings/RecordingsListPage";
import BatchMaterialsPage from "../Materials/BatchMaterialsPage";
import StudentSyllabus from "./Syllabus/StudentSyllabus";
import StudentGeneralAssignments from "../Assignments/general/StudentGeneralAssignments";
import apiClient from "../../utils/apiClient";
import { getCurrentUser } from "../../utils/auth";
import { usePlan } from "../../contexts/PlanContext";
import "./StudentLearningHub.scss";

/**
 * StudentLearningHub
 *
 * Unified page combining Classes (Recordings), Materials, and Syllabus
 * into a single tabbed interface for students.
 *
 * Route: /s/learning
 * Supports ?tab=classes|materials|syllabus query param
 */
const StudentLearningHub = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPlanFeature } = usePlan();

  const showClasses = hasPlanFeature("recordings");

  // Build tab definitions
  const tabs = useMemo(() => {
    const list = [];
    if (showClasses) list.push({ key: "classes", label: "Classes", icon: Video });
    list.push({ key: "materials", label: "Materials", icon: FileText });
    list.push({ key: "syllabus", label: "Syllabus", icon: BookOpen });
    list.push({ key: "assignments", label: "Assignments", icon: ClipboardList });
    return list;
  }, [showClasses]);

  // Determine initial tab from query param
  const params = new URLSearchParams(location.search);
  const tabParam = params.get("tab");
  const validKeys = tabs.map((t) => t.key);
  const initialTab = validKeys.includes(tabParam) ? tabParam : tabs[0]?.key;

  const [activeTab, setActiveTab] = useState(initialTab);

  // ── Multi-course enrollment data ──
  const user = getCurrentUser();
  const studentId = user?.studentId;
  const [enrollments, setEnrollments] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const selected = enrollments[selectedIdx] || null;
  const batchId = selected?.batchId?._id || selected?.batchId || null;
  const courseId = selected?.courseId?._id || selected?.courseId || null;
  const hasMultiple = enrollments.length > 1;

  // Fetch enrollments from CourseAssignment, fallback to student profile
  useEffect(() => {
    const load = async () => {
      setProfileLoading(true);
      setProfileError("");
      try {
        // Try multi-course endpoint first
        const { data } = await apiClient.get("/course-assignments/my-enrollments");
        const list = data?.data || [];
        if (list.length > 0) {
          setEnrollments(list);
          setProfileLoading(false);
          return;
        }
      } catch { /* fallback below */ }

      // Fallback: single-course from student profile
      try {
        if (!studentId) throw new Error();
        const { data } = await apiClient.get(`/students/${studentId}`);
        const student = data.student;
        if (student?.batch) {
          const bId = student.batch._id || student.batch;
          const cId = student.batch?.course?._id || student.batch?.course || student.course?._id || student.course;
          const courseName = student.batch?.course?.name || student.course?.name || "";
          const batchName = student.batch?.name || "";
          setEnrollments([{
            _id: "fallback",
            batchId: { _id: bId, name: batchName },
            courseId: { _id: cId, name: courseName },
            isPrimary: true,
          }]);
        } else {
          setProfileError("You are not enrolled in any batch yet.");
        }
      } catch {
        setProfileError("Failed to load your batch information.");
      }
      setProfileLoading(false);
    };

    load();
  }, [studentId]);

  const handleSelectCourse = useCallback((idx) => {
    setSelectedIdx(idx);
    setDropdownOpen(false);
  }, []);

  // Sync tab to URL
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`/s/learning?tab=${tab}`, { replace: true });
  };

  const courseSwitcher = hasMultiple ? (
    <div className="slh-course-switcher">
      <span className="slh-course-switcher__label">Course</span>
      <div className="slh-course-switcher__current" onClick={() => setDropdownOpen(o => !o)}>
        <div className="slh-course-switcher__info">
          <span className="slh-course-switcher__course-name">
            {selected?.courseId?.name || "Select Course"}
          </span>
          {selected?.batchId?.name && (
            <span className="slh-course-switcher__batch-name">{selected.batchId.name}</span>
          )}
        </div>
        <ChevronDown size={16} className={`slh-course-switcher__chevron ${dropdownOpen ? "slh-course-switcher__chevron--open" : ""}`} />
      </div>
      {dropdownOpen && (
        <>
          <div className="slh-course-switcher__backdrop" onClick={() => setDropdownOpen(false)} />
          <ul className="slh-course-switcher__dropdown">
            {enrollments.map((enr, i) => (
              <li
                key={enr._id}
                className={`slh-course-switcher__option ${i === selectedIdx ? "slh-course-switcher__option--active" : ""}`}
                onClick={() => handleSelectCourse(i)}
              >
                <span className="slh-course-switcher__option-course">{enr.courseId?.name || "Course"}</span>
                {enr.batchId?.name && (
                  <span className="slh-course-switcher__option-batch">{enr.batchId.name}</span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  ) : null;

  return (
    <div className="student-learning-hub">
      <PageHeader
        title="My Learning"
        description="Access your class recordings, study materials, and syllabus"
        icon={GraduationCap}
        actions={courseSwitcher}
      />

      {/* Tab navigation */}
      <Tabs value={activeTab} onChange={handleTabChange} className="slh-tabs mt-3">
        <TabsList className="slh-tabs-list">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <TabsTrigger key={tab.key} value={tab.key} className="gap-2">
                <Icon size={15} />
                <span>{tab.label}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {showClasses && (
          <TabsContent value="classes">
            <div className="slh-tab-panel">
              <RecordingsListPage batchId={batchId} />
            </div>
          </TabsContent>
        )}

        <TabsContent value="materials">
          <div className="slh-tab-panel">
            {profileLoading ? (
              <div className="text-center py-5">
                <Spinner animation="border" />
              </div>
            ) : profileError || !batchId ? (
              <div className="slh-empty-msg">
                <FileText size={32} className="text-muted mb-2" />
                <p className="text-muted">
                  {profileError || "You are not enrolled in any batch yet."}
                </p>
              </div>
            ) : (
              <BatchMaterialsPage batchId={batchId} courseId={courseId} role="student" />
            )}
          </div>
        </TabsContent>

        <TabsContent value="syllabus">
          <div className="slh-tab-panel">
            <StudentSyllabus batchId={batchId} />
          </div>
        </TabsContent>

        <TabsContent value="assignments">
          <div className="slh-tab-panel">
            <StudentGeneralAssignments batchId={batchId} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StudentLearningHub;
